import { zodResolver } from "@hookform/resolvers/zod";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import {
  Avatar,
  Button,
  CheckboxInput,
  DropdownSelect,
  ErrorMessage,
  Icon,
  PhoneNumberInput,
  Spinner,
  TextInput,
  Typography,
} from "@/components/atoms";
import { EMAIL_PATTERN, TITLE_OPTIONS } from "@/components/organisms/MyFavourites/GuestPassengersTable/guestForm";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { stashGuestDraft } from "@/components/pages/Guest/guestDraftStash";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useAddFavouritePassengerMutation, useListFavouritePassengersQuery } from "@/redux/apis/favouritePassenger/favouritePassengerApi";
import { FavouritePassenger } from "@/redux/apis/favouritePassenger/types";
import { useVerifyGuestEmailMutation } from "@/redux/apis/guest/guestApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { addSessionGuest, setBookingPassenger, setGuestContact } from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectBookingPassenger,
  selectClientBookingDraft,
  selectEditJobId,
  selectGuestContact,
  selectSessionGuests,
} from "@/redux/slices/clientBooking/selectors";
import { BookingPassenger } from "@/redux/slices/clientBooking/types";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { addToast, clsx } from "@/utils";
import { useAccountHolder } from "./useAccountHolder";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";
import { useForceValidate } from "./useForceValidate";

type WhoLayout = "card" | "sheet";

interface PassengerPanelProps {
  /** "card" = desktop side panel; "sheet" = mobile bottom sheet (adds a Proceed/Close footer). */
  layout: WhoLayout;
  onClose: () => void;
  /**
   * "card" layout only: called after the details are saved so the wizard opens the next Trip Details
   * sub-step (Date & Time). The "sheet" layout closes back to the list instead (mobile behaviour).
   */
  onAdvance?: () => void;
  /** When true, show the guest contact field errors immediately (routed here by a failed exit).
   * No-op for the authed flow, whose passenger selection is always valid. */
  forceValidate?: boolean;
}

const guestSchema = z.object({
  title: z.string().min(1, "Title not selected"),
  firstName: z.string().min(1, "First Name is required"),
  lastName: z.string().min(1, "Last Name is required"),
  // Same regex as the old passenger booking form / app-customer, not zod's stricter .email().
  email: z.string().min(1, "Email is required").regex(EMAIL_PATTERN, "Invalid email address"),
  contactNumber: z
    .string()
    .min(1, "Please fill out a phone number")
    .refine((value) => isValidPhoneNumber(value ?? ""), "Invalid phone number"),
  saveToFavourites: z.boolean(),
});
type GuestFormData = z.infer<typeof guestSchema>;

const EMPTY_FORM: GuestFormData = { title: "", firstName: "", lastName: "", email: "", contactNumber: "", saveToFavourites: false };

const favouriteToPassenger = (p: FavouritePassenger): BookingPassenger => ({
  id: p.uuid,
  title: p.title,
  firstName: p.first_name,
  lastName: p.last_name,
  phone: p.phone,
  email: p.email,
  kind: "favourite",
});

// `selfSuffix` is the account-holder tag from useAccountHolder — " (You)" in self-service, "" when an
// operator/EA is booking for a managed client (the "self" row is then the client, named plainly).
const displayName = (p: BookingPassenger, selfSuffix = " (You)") =>
  `${p.firstName} ${p.lastName}${p.kind === "self" ? selfSuffix : " (Guest)"}`;

/** Round avatar for a passenger row — the account holder's photo, or a person glyph for guests. */
const PassengerAvatar = ({ passenger, avatar, selfSuffix }: { passenger: BookingPassenger; avatar?: string; selfSuffix?: string }) =>
  passenger.kind === "self" ? (
    <Avatar size="xs" src={avatar} alt={displayName(passenger, selfSuffix)} />
  ) : (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-mid-gray">
      <Icon name="Profile" size="sm" variant="Bold" className="text-neutral-silver-gray" />
    </span>
  );

/** A selectable passenger row. Selected rows show a green success check on the right. */
const PassengerRow = ({ selected, onClick, children }: React.PropsWithChildren<{ selected?: boolean; onClick: () => void }>) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      "flex w-full items-center gap-3 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-3 text-left",
      // No hover affordance on the already-selected row — only rows you can switch to.
      !selected && "hover:bg-neutral-gray"
    )}
  >
    <span className="flex min-w-0 flex-1 items-center gap-2">{children}</span>
    {selected && <Icon name="TickCircle" variant="Bold" size="lg" className="shrink-0 text-success" />}
  </button>
);

/**
 * "Who is this booking for?" — the Passenger sub-panel of the Trip Details step. Lists the account
 * holder ("You") + favourite guests + guests added this session, plus a "New Guest" form. Selection
 * and session guests live in clientBookingSlice so they survive the panel closing/reopening (but not
 * a refresh — matching the design note that unsaved guests don't persist).
 */
const AuthedPassengerPanel = ({ layout, onClose, onAdvance }: PassengerPanelProps) => {
  const dispatch = useAppDispatch();
  const viewOnly = useBookingViewOnly();
  const accountHolder = useAccountHolder();
  // Edit relabels the plain sheet CTA "Confirm" (it closes the sheet). "Proceed with Guest" is kept.
  const isEdit = useAppSelector(selectEditJobId) != null;
  const selected = useAppSelector(selectBookingPassenger);
  const sessionGuests = useAppSelector(selectSessionGuests);
  const { accountId } = useClientScope();

  const { data: favourites = [], isLoading: isLoadingFavourites } = useListFavouritePassengersQuery({ accountId });
  const [addPassenger, { isLoading: isSaving }] = useAddFavouritePassengerMutation();

  const [mode, setMode] = useState<"select" | "new-guest">("select");

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GuestFormData>({ resolver: zodResolver(guestSchema), defaultValues: EMPTY_FORM });

  const self: BookingPassenger = {
    id: "self",
    title: accountHolder.title,
    firstName: accountHolder.firstName,
    lastName: accountHolder.lastName,
    phone: accountHolder.mobile,
    email: accountHolder.email,
    kind: "self",
  };

  const rows: BookingPassenger[] = [self, ...favourites.map(favouriteToPassenger), ...sessionGuests];
  // `null` selection ⇒ the account holder is selected by default.
  const isSelected = (p: BookingPassenger) => (selected ? selected.id === p.id : p.kind === "self");

  const selectPassenger = (p: BookingPassenger) => dispatch(setBookingPassenger(p.kind === "self" ? null : p));

  const backToList = () => {
    reset(EMPTY_FORM);
    setMode("select");
  };

  const onProceedGuest = (form: GuestFormData) => {
    const base = {
      title: form.title,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.contactNumber,
      email: form.email.trim(),
    };

    if (form.saveToFavourites) {
      addPassenger({
        accountId,
        title: base.title,
        first_name: base.firstName,
        last_name: base.lastName,
        phone: base.phone,
        email: base.email,
      })
        .unwrap()
        .then((saved) => {
          // Saved guests come back through the favourites query; select the persisted record.
          dispatch(setBookingPassenger({ ...base, id: saved.uuid, kind: "favourite" }));
          backToList();
          if (layout === "sheet") onClose();
          else onAdvance?.();
        })
        .catch((error) => addToast("danger", getErrorMessages(error)[0]));
      return;
    }

    const guest: BookingPassenger = { ...base, id: `guest-${Date.now()}`, kind: "guest" };
    dispatch(addSessionGuest(guest));
    dispatch(setBookingPassenger(guest));
    backToList();
    if (layout === "sheet") onClose();
    else onAdvance?.();
  };

  const isNewGuest = mode === "new-guest";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Padding lives here (not on the outer card/sheet) so the divider can bleed full-width via -mx-5. */}
      <ViewOnlyFieldset>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
        <Typography variant="h2">Who is this booking for?</Typography>
        <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
          Select the passenger for this trip
        </Typography>

        <Typography variant="action" className="mt-6 block">
          Select the passenger for this booking
        </Typography>

        {isNewGuest ? (
          <>
            {/* New Guest chip — ✕ cancels back to the list. */}
            <div className="mt-2 flex items-center gap-3 rounded-lg border border-neutral-mid-gray bg-neutral-gray p-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-mid-gray">
                <Icon name="Add" size="md" className="text-neutral-dark-gray" />
              </span>
              <Typography variant="paragraph" className="min-w-0 flex-1 truncate text-neutral-dark-gray">
                New Guest
              </Typography>
              <button
                type="button"
                aria-label="Cancel new guest"
                onClick={backToList}
                className="shrink-0 text-neutral-dark-gray hover:text-danger"
              >
                <Icon name="Add" size="md" className="rotate-45" />
              </button>
            </div>

            {/* Full-bleed divider (cancels the body's px-5). 30px below the chip, 24px above the form.
                shrink-0 so it isn't collapsed to 0 when error messages grow the scroll content. */}
            <div className="-mx-5 mt-[30px] h-px shrink-0 bg-neutral-mid-gray" />

            <form className="mt-6 flex flex-col gap-6" onSubmit={handleSubmit(onProceedGuest)}>
              <div className="flex flex-col gap-1">
                <Typography variant="action" className="text-neutral-dark-gray">
                  Title
                </Typography>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <DropdownSelect
                      className="w-full"
                      size="md"
                      hasError={!!errors.title}
                      placeholder="Select Title"
                      options={TITLE_OPTIONS}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                  )}
                />
                <ErrorMessage errors={errors} name="title" />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="who-first-name">
                  <Typography variant="action" className="text-neutral-dark-gray">
                    First Name
                  </Typography>
                </label>
                <TextInput
                  id="who-first-name"
                  size="md"
                  placeholder="Enter First Name"
                  hasError={!!errors.firstName}
                  {...register("firstName")}
                />
                <ErrorMessage errors={errors} name="firstName" />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="who-last-name">
                  <Typography variant="action" className="text-neutral-dark-gray">
                    Last Name
                  </Typography>
                </label>
                <TextInput
                  id="who-last-name"
                  size="md"
                  placeholder="Enter Last Name"
                  hasError={!!errors.lastName}
                  {...register("lastName")}
                />
                <ErrorMessage errors={errors} name="lastName" />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="who-email">
                  <Typography variant="action" className="text-neutral-dark-gray">
                    Email
                  </Typography>
                </label>
                <TextInput
                  id="who-email"
                  type="email"
                  size="md"
                  placeholder="Enter Email Address"
                  hasError={!!errors.email}
                  {...register("email")}
                />
                <ErrorMessage errors={errors} name="email" />
              </div>

              <div className="flex flex-col gap-1">
                <Typography variant="action" className="text-neutral-dark-gray">
                  Contact Number #1
                </Typography>
                <PhoneNumberInput control={control} name="contactNumber" size="md" hasError={!!errors.contactNumber} />
                <ErrorMessage errors={errors} name="contactNumber" />
              </div>

              <Controller
                name="saveToFavourites"
                control={control}
                render={({ field }) => (
                  // labelClassName bumps the weight to match Typography `action` (CheckboxInput's label is `paragraph`).
                  <CheckboxInput
                    label="Save this guest to My Favourites"
                    labelClassName="font-semibold"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              {/* Desktop: the proceed action lives at the bottom of the form (24px below via gap-6). */}
              {layout === "card" && !viewOnly && (
                <Button variant="primary" size="lg" className="w-full" disabled={isSaving} onClick={handleSubmit(onProceedGuest)}>
                  {isSaving ? <Spinner variant="light" /> : "Proceed with Guest"}
                </Button>
              )}
            </form>
          </>
        ) : (
          <div className="mt-2 flex flex-col gap-2">
            {/* View-only: only the passenger this booking was for — no other options, no New Guest. */}
            {(viewOnly ? rows.filter((p) => isSelected(p)) : rows).map((p) => (
              <PassengerRow key={p.id} selected={isSelected(p)} onClick={() => selectPassenger(p)}>
                <PassengerAvatar passenger={p} avatar={accountHolder.avatar} selfSuffix={accountHolder.selfSuffix} />
                <Typography variant="paragraph" className="min-w-0 truncate text-neutral-dark-gray">
                  {displayName(p, accountHolder.selfSuffix)}
                </Typography>
              </PassengerRow>
            ))}

            {/* Self is instant (from the profile); the spinner marks the favourite guests still loading. */}
            {!viewOnly && isLoadingFavourites && (
              <div className="flex justify-center py-3">
                <Spinner />
              </div>
            )}

            {!viewOnly && (
              <PassengerRow onClick={() => setMode("new-guest")}>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-mid-gray">
                  <Icon name="Add" size="md" className="text-neutral-dark-gray" />
                </span>
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  New Guest
                </Typography>
              </PassengerRow>
            )}
          </div>
        )}
      </div>
      </ViewOnlyFieldset>

      {/* Card select mode: a Proceed CTA so picking an existing passenger advances to the next sub-step
          (the account holder is selected by default, so it's always actionable). Matches the other card
          sub-panels' footer + the mobile sheet below. New Guest has its own in-form CTA; view-only has none. */}
      {layout === "card" && !isNewGuest && !viewOnly && (
        <div className="flex flex-col gap-2 px-5 pb-5">
          <Button variant="primary" size="lg" className="w-full" onClick={() => onAdvance?.()}>
            Confirm
          </Button>
        </div>
      )}

      {/* Mobile bottom-sheet footer: contextual primary + Close, pinned below a full-width divider.
          24px above the buttons; 12px below Close to the sheet's bottom edge. */}
      {layout === "sheet" && !viewOnly && (
        <div className="flex flex-col gap-2 border-t border-neutral-mid-gray px-5 pb-3 pt-6">
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={isSaving}
            onClick={isNewGuest ? handleSubmit(onProceedGuest) : onClose}
          >
            {isSaving ? <Spinner variant="light" /> : isNewGuest ? "Proceed with Guest" : isEdit ? "Confirm" : "Proceed"}
          </Button>
          <Button variant="custom" size="md" className="text-info" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

// The guest self-details form reuses the New Guest fields minus "save to favourites" (a guest has no
// account to save into). Same EMAIL_PATTERN + phone validation as the authed guest form.
const guestContactSchema = z.object({
  title: z.string().min(1, "Title not selected"),
  firstName: z.string().min(1, "First Name is required"),
  lastName: z.string().min(1, "Last Name is required"),
  email: z.string().min(1, "Email is required").regex(EMAIL_PATTERN, "Invalid email address"),
  contactNumber: z
    .string()
    .min(1, "Please fill out a phone number")
    .refine((value) => isValidPhoneNumber(value ?? ""), "Invalid phone number"),
});
type GuestContactFormData = z.infer<typeof guestContactSchema>;

/**
 * Guest flow "Who is this booking for?". The guest has no account, favourites, or profile,
 * so instead of the passenger picker it collects the guest's OWN details — title, first/last name,
 * EMAIL, phone. These are the account-mint source: stored on the draft via setGuestContact and sent
 * top-level on the guest booking-create body. Prefilled from any details already captured so
 * re-opening the step keeps them.
 */
const GuestSelfPassengerPanel = ({ layout, onClose, onAdvance, forceValidate = false }: PassengerPanelProps) => {
  const dispatch = useAppDispatch();
  const { login } = useKindeAuth();
  const contact = useAppSelector(selectGuestContact);
  const draft = useAppSelector(selectClientBookingDraft);
  const operator = useAppSelector(activeOperatorSelector);
  const [verifyEmail, { isLoading: isCheckingEmail }] = useVerifyGuestEmailMutation();
  // Set once the entered email is found to already belong to an account — blocks proceeding + prompts login.
  const [emailTaken, setEmailTaken] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<GuestContactFormData>({
    resolver: zodResolver(guestContactSchema),
    defaultValues: {
      title: contact?.title ?? "",
      firstName: contact?.firstName ?? "",
      lastName: contact?.lastName ?? "",
      email: contact?.email ?? "",
      contactNumber: contact?.phone ?? "",
    },
  });

  // Routed here by a failed "Next Step": surface the required guest-contact field errors now.
  useForceValidate(forceValidate, () => void trigger());

  // Does this email already belong to an account? A 2xx from the verify endpoint = yes; a 403 (or any
  // error) = treat as free — the create-time ACCOUNT_EXISTS 409 is the backstop, so we fail open on
  // transient errors rather than wrongly blocking a valid guest.
  const checkEmailExists = async (email: string): Promise<boolean> => {
    if (!email || !EMAIL_PATTERN.test(email)) return false;
    try {
      const res = await verifyEmail(email).unwrap();
      // Contract: existing account → { success: true }. Key on the body (not just 2xx) so a hypothetical
      // 200 { success: false } for a free email doesn't wrongly block the guest.
      return res.success === true;
    } catch {
      return false;
    }
  };

  // Early feedback: on blur, flag an already-registered email (doesn't block typing; the gate is onProceed).
  const onEmailBlur = async () => setEmailTaken(await checkEmailExists(getValues("email").trim()));

  // Send the existing user straight to the Kinde hosted login (deep-link), skipping our internal
  // /login page, stashing the in-progress draft so it resumes after they authenticate. Kinde returns
  // to /auth-callback → AuthCallback routes the (now-authed) client to /client → ClientPortal sees the
  // stash and forwards to /client/booking, where ClientBooking restores the draft. login() takes no
  // org/connection params — it uses the KindeProvider config already seeded by ConfigProvider on the
  // public guest route (the operator is resolved by the time a guest reaches this step).
  const goToLogin = () => {
    stashGuestDraft(draft);
    login({});
  };

  const onProceed = async (form: GuestContactFormData) => {
    // An existing account can't guest-book — send them to log in instead of building a doomed booking.
    if (await checkEmailExists(form.email.trim())) {
      setEmailTaken(true);
      return;
    }
    dispatch(
      setGuestContact({
        title: form.title,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.contactNumber,
      })
    );
    addToast("success", "Your details are saved for this booking");
    if (layout === "sheet") onClose();
    else onAdvance?.();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
        <Typography variant="h2">Who is this booking for?</Typography>
        <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
          Enter your details to complete this booking
        </Typography>

        <form className="mt-6 flex flex-col gap-6" onSubmit={handleSubmit(onProceed)}>
          <div className="flex flex-col gap-1">
            <Typography variant="action" className="text-neutral-dark-gray">
              Title
            </Typography>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <DropdownSelect
                  className="w-full"
                  size="md"
                  hasError={!!errors.title}
                  placeholder="Select Title"
                  options={TITLE_OPTIONS}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
            <ErrorMessage errors={errors} name="title" />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="guest-first-name">
              <Typography variant="action" className="text-neutral-dark-gray">
                First Name
              </Typography>
            </label>
            <TextInput
              id="guest-first-name"
              size="md"
              placeholder="Enter First Name"
              hasError={!!errors.firstName}
              {...register("firstName")}
            />
            <ErrorMessage errors={errors} name="firstName" />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="guest-last-name">
              <Typography variant="action" className="text-neutral-dark-gray">
                Last Name
              </Typography>
            </label>
            <TextInput
              id="guest-last-name"
              size="md"
              placeholder="Enter Last Name"
              hasError={!!errors.lastName}
              {...register("lastName")}
            />
            <ErrorMessage errors={errors} name="lastName" />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="guest-email">
              <Typography variant="action" className="text-neutral-dark-gray">
                Email
              </Typography>
            </label>
            <TextInput
              id="guest-email"
              type="email"
              size="md"
              placeholder="Enter Email Address"
              // An existing-account email isn't an input error — the welcome-back card conveys it, so
              // don't turn the field red; only real validation errors do.
              hasError={!!errors.email}
              {...register("email", {
                // Clear the "already registered" flag as soon as they edit the email; re-check on blur.
                onChange: () => emailTaken && setEmailTaken(false),
                onBlur: () => void onEmailBlur(),
              })}
            />
            <ErrorMessage errors={errors} name="email" />
            {emailTaken && (
              // Existing-user prompt (Figma 6171-34346): the entered email already belongs to an
              // account, so instead of a doomed guest booking we invite them to log in — a direct Kinde
              // deep-link that resumes this booking on return (see goToLogin).
              <div className="mt-2 flex flex-col gap-4 rounded-lg border border-vivid-purple bg-vivid-purple/10 p-3">
                <div className="flex flex-col gap-1">
                  <Typography variant="action" className="text-vivid-purple-dark">
                    {operator?.name
                      ? `Welcome back! You're already a client of ${operator.name}.`
                      : "Welcome back! You're already a client."}
                  </Typography>
                  <Typography variant="paragraph" className="text-vivid-purple-dark">
                    Log in to automatically fill in your details and keep all your bookings together.
                  </Typography>
                </div>
                <Button variant="primary" size="sm" className="w-full" onClick={goToLogin}>
                  Login
                </Button>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Typography variant="action" className="text-neutral-dark-gray">
              Contact Number #1
            </Typography>
            <PhoneNumberInput control={control} name="contactNumber" size="md" hasError={!!errors.contactNumber} />
            <ErrorMessage errors={errors} name="contactNumber" />
          </div>

          {layout === "card" && (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isCheckingEmail || emailTaken}
              onClick={handleSubmit(onProceed)}
            >
              {isCheckingEmail ? <Spinner variant="light" /> : "Save Details"}
            </Button>
          )}
        </form>
      </div>

      {layout === "sheet" && (
        <div className="flex flex-col gap-2 border-t border-neutral-mid-gray px-5 pb-3 pt-6">
          <Button variant="primary" size="lg" className="w-full" disabled={isCheckingEmail || emailTaken} onClick={handleSubmit(onProceed)}>
            {isCheckingEmail ? <Spinner variant="light" /> : "Save Details"}
          </Button>
          <Button variant="custom" size="md" className="text-info" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Passenger step dispatcher. The authed path (`AuthedPassengerPanel`) is unchanged — the account
 * holder + favourites + session guests picker. The guest flow (`useGuestBooking().isGuest`) swaps in
 * a self-details form that captures the guest's own contact details (the account-mint source). Each
 * variant is its own component so hook order stays unconditional.
 */
export const PassengerPanel = (props: PassengerPanelProps) => {
  const { isGuest } = useGuestBooking();
  return isGuest ? <GuestSelfPassengerPanel {...props} /> : <AuthedPassengerPanel {...props} />;
};
